import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowUpRight, Plus, Minus, Cpu, Film, BookOpen, Printer, TrendingUp, Sparkles, Layers } from 'lucide-react';
import { skillsCapabilities } from '../data';
import useIsMobile from '../hooks/useIsMobile';

const getTagsForSkill = (id: string) => {
  switch (id) {
    case 'skill-consultancy':
      return [
        { name: 'Direct Discussion', color: 'text-emerald-500', icon: Sparkles },
        { name: 'Creative Consult', color: 'text-indigo-500', icon: Layers },
        { name: 'Scope Blueprint', color: 'text-amber-500', icon: Sparkles },
        { name: 'Video Strategy', color: 'text-blue-500', icon: Film }
      ];
    case 'skill-1':
      return [
        { name: 'Adobe Suite', color: 'text-amber-500', icon: Sparkles },
        { name: 'Identity Guidelines', color: 'text-emerald-500', icon: Layers },
        { name: 'Static & Motion', color: 'text-blue-500', icon: Film },
        { name: 'Canva', color: 'text-sky-500', icon: Sparkles }
      ];
    case 'skill-2':
      return [
        { name: 'AI Design Workflows', color: 'text-purple-500', icon: Sparkles },
        { name: 'Motion / React', color: 'text-cyan-500', icon: Film },
        { name: 'Fast E-Comm Stores', color: 'text-emerald-500', icon: Cpu },
        { name: 'Web Apps & Games', color: 'text-rose-500', icon: Sparkles }
      ];
    case 'skill-3':
      return [
        { name: 'Campaign Strategy', color: 'text-indigo-500', icon: Sparkles },
        { name: 'Meta & Google Ads', color: 'text-pink-500', icon: Layers },
        { name: 'Conversion Design', color: 'text-emerald-500', icon: Cpu },
        { name: 'Print Design', color: 'text-blue-500', icon: Printer }
      ];
    case 'skill-4':
      return [
        { name: 'Multi-Platform Kit', color: 'text-rose-500', icon: TrendingUp },
        { name: 'Short & Long form', color: 'text-purple-500', icon: Film },
        { name: 'Trend Synthesis', color: 'text-amber-500', icon: Sparkles },
        { name: 'Static & Motion', color: 'text-blue-500', icon: Film },
        { name: 'Meta & Google Ads', color: 'text-pink-500', icon: Layers }
      ];
    case 'skill-5':
      return [
        { name: 'Adobe InDesign', color: 'text-pink-600', icon: Layers },
        { name: 'Book Layout Pro', color: 'text-amber-600', icon: BookOpen },
        { name: 'Grid Hierarchies', color: 'text-cyan-600', icon: Layers },
        { name: 'Illustrator', color: 'text-amber-500', icon: Sparkles },
        { name: 'Canva', color: 'text-sky-500', icon: Sparkles }
      ];
    case 'skill-6':
      return [
        { name: 'CMYK/Pantones', color: 'text-orange-500', icon: Printer },
        { name: 'Bindings', color: 'text-emerald-500', icon: BookOpen },
        { name: 'Prepress Workflows', color: 'text-cyan-500', icon: Layers },
        { name: 'OOH', color: 'text-blue-500', icon: Sparkles }
      ];
    case 'skill-8':
      return [
        { name: '3D Mockups', color: 'text-purple-500', icon: Sparkles },
        { name: 'Dielines & Structure', color: 'text-cyan-500', icon: Layers },
        { name: 'Retail Presentation', color: 'text-emerald-500', icon: Cpu },
        { name: 'Print Finishes', color: 'text-orange-500', icon: Printer }
      ];
    default:
      return [
        { name: 'Creative Execution', color: 'text-emerald-500', icon: Sparkles }
      ];
  }
};

export default function Capabilities() {
  const [expandedId, setExpandedId] = useState<string | null>('skill-consultancy');
  const isMobile = useIsMobile();

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section id="services" className="py-16 scroll-mt-24 relative overflow-hidden bg-transparent text-neutral-900 dark:text-white transition-colors duration-500">
      {/* Background Ambience / Blur Overlays replicating premium high-end portfolio layout */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-950/20 via-neutral-950/20 to-neutral-950 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -top-40 right-20 w-[450px] h-[450px] bg-emerald-500/5 blur-[130px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -bottom-40 left-10 w-[550px] h-[550px] bg-emerald-500/5 blur-[150px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Section Header */}
        <div className="mb-10 max-w-xl">
          <h2 className="text-2xl sm:text-4xl font-sans tracking-tight text-neutral-900 dark:text-white font-medium hover:text-emerald-500 transition-colors duration-300 cursor-default">
            Areas of Expertise
          </h2>
          <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 font-sans mt-2 font-light leading-relaxed">
            Jahangir is a multidisciplinary artist specializing in creative direction, motion design, static visuals, and 2D animation.
          </p>
        </div>

        {/* Stacked Interactive Accordions */}
        <div className="border-t border-neutral-200 dark:border-neutral-800">
          {skillsCapabilities.map((skill, index) => {
            const isExpanded = expandedId === skill.id;

            return (
              <motion.div
                key={skill.id}
                initial={isMobile ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 60, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: false, amount: 0.08 }}
                transition={isMobile ? { duration: 0.2 } : { type: "spring", stiffness: 80, damping: 14, delay: Math.min(index * 0.07, 0.45) }}
                className={`border-b border-neutral-200 dark:border-neutral-800 transition-all duration-300 ${isMobile ? '' : 'hover:scale-[1.01]'} origin-center`}
              >
                {/* Accordion Head Header */}
                <button
                  onClick={() => toggleExpand(skill.id)}
                  className="w-full text-left py-5 flex items-center justify-between gap-4 group"
                  data-cursor="hover"
                >
                  <div className="flex items-center gap-4 sm:gap-8 flex-grow">
                    {/* Index Number */}
                    <span className="font-mono text-xs font-bold text-neutral-500 dark:text-neutral-450 group-hover:text-emerald-500 transition-colors bg-neutral-100 dark:bg-neutral-900 px-2 py-1 rounded">
                      {skill.number}
                    </span>

                    {/* Left Column Text */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 flex-grow">
                      <h3 className={`text-base sm:text-lg font-sans font-semibold tracking-tight transition-colors group-hover:text-emerald-500 ${
                        isExpanded
                          ? 'text-emerald-500 dark:text-emerald-400'
                          : 'text-neutral-950 dark:text-white'
                      }`}>
                        {skill.title}
                      </h3>
                      <p className="hidden md:block text-[11px] text-neutral-500 dark:text-neutral-400 pr-8 line-clamp-1 self-center">
                        {skill.description}
                      </p>
                    </div>
                  </div>

                  {/* Toggle Indicator Button */}
                  <div className={`p-1.5 rounded-full border transition-all duration-300 ${
                    isExpanded
                      ? 'border-emerald-500 bg-emerald-500 text-white'
                      : 'border-neutral-200 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 dark:group-hover:border-neutral-700'
                  }`}>
                    {isExpanded ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                  </div>
                </button>

                {/* Accordion Slide Panel */}
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
                      className="overflow-hidden"
                    >
                      {/* Body Inner content */}
                      <div className="pb-6 pl-8 sm:pl-16 pr-4 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                        {/* Left Info written summary */}
                        <div className="lg:col-span-7 space-y-4">
                          <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed font-light">
                            {skill.detailedDescription}
                          </p>

                          {/* Matching Software Tools Tags */}
                          <div className="flex flex-wrap gap-1.5 pt-2">
                            {getTagsForSkill(skill.id).map((tag, tIdx) => {
                              const TagIcon = tag.icon;
                              return (
                                <span key={tIdx} className="font-mono text-[9px] font-bold text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                                  <TagIcon className={`w-2.5 h-2.5 ${tag.color}`} /> {tag.name}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        {/* Right lists of specific capabilities */}
                        <div className="lg:col-span-5 bg-neutral-50 dark:bg-neutral-900/15 border border-neutral-100 dark:border-neutral-800/40 p-4 rounded-xl">
                          <h4 className="font-mono text-[9px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-widest block mb-2">
                            PRO DELIVERABLES
                          </h4>
                          <ul className="space-y-1.5 mb-4">
                            {skill.listItems.map((item, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-neutral-700 dark:text-neutral-300">
                                <span className="h-1 w-1 bg-emerald-500 rounded-full flex-shrink-0" />
                                <span className="text-[11px] sm:text-xs font-medium tracking-wide">{item}</span>
                              </li>
                            ))}
                          </ul>
                          <a
                            href="#contact"
                            className="inline-flex w-full items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-bold tracking-wider font-mono uppercase bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white dark:text-neutral-950 dark:bg-emerald-400 dark:hover:bg-emerald-300 dark:active:bg-emerald-500 rounded-lg transition-transform hover:scale-[1.01] active:scale-[0.99] duration-150 group/btn"
                            data-cursor="hover"
                          >
                            Book Now
                            <ArrowUpRight className="w-3.5 h-3.5 transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform duration-200" />
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
